import { useCallback, useMemo } from 'react'

import { format } from 'date-fns'
import {
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs'

import { useToday } from '@/hooks/use-today'

import type { DateRange, DateRangePreset } from './types'
import {
  DATE_RANGE_PRESETS,
  getPresetRange,
  readCustomDateRange,
} from './utils'

export interface CalendarDayRange {
  from: string
  to: string
}

export function getDefaultDateRange(
  preset: DateRangePreset,
  now?: Date,
): DateRange {
  return { ...getPresetRange(preset, now), preset }
}

export function useDateRangeQueryState(defaultPreset: DateRangePreset) {
  const today = useToday()
  const [params, setParams] = useQueryStates(
    {
      preset:
        parseAsStringLiteral(DATE_RANGE_PRESETS).withDefault(defaultPreset),
      from: parseAsString,
      to: parseAsString,
      page: parseAsInteger.withDefault(1),
    },
    {
      history: 'push',
      shallow: false,
    },
  )

  const { preset, from, to } = params

  const dateRange: DateRange = useMemo(() => {
    if (preset === 'custom') {
      return (
        readCustomDateRange(from, to) ??
        getDefaultDateRange(defaultPreset, today)
      )
    }
    return { ...getPresetRange(preset, today), preset }
  }, [preset, from, to, defaultPreset, today])

  const period: CalendarDayRange = useMemo(
    () => ({
      from: format(dateRange.from, 'yyyy-MM-dd'),
      to: format(dateRange.to, 'yyyy-MM-dd'),
    }),
    [dateRange],
  )

  const setDateRange = useCallback(
    (range: DateRange) => {
      if (range.preset && range.preset !== 'custom') {
        setParams({ preset: range.preset, from: null, to: null, page: 1 })
      } else {
        setParams({
          preset: 'custom',
          from: range.from.toISOString(),
          to: range.to.toISOString(),
          page: 1,
        })
      }
    },
    [setParams],
  )

  const resetDateRange = useCallback(
    () => setParams({ preset: defaultPreset, from: null, to: null, page: 1 }),
    [setParams, defaultPreset],
  )

  return {
    dateRange,
    setDateRange,
    period,
    preset,
    resetDateRange,
  }
}
